import axios from "axios";
import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiSend,
  FiCheckCircle,
  FiAlertCircle,
  FiFacebook,
  FiTwitter,
  FiLinkedin,
  FiInstagram,
  FiGlobe,
} from "react-icons/fi";
import {
  animationVariants,
  motionProps,
  transitions,
} from "../../hooks/useOptimizedAnimation";

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post("/api/contact", formData);
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending message:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-green-800 via-green-700 to-green-800">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
        </div>

        <div className="relative px-4 py-20 md:px-8 lg:px-16">
          <div className="mx-auto max-w-7xl">
            <div className="text-center text-white">
              <h1 className="mb-6 text-5xl font-bold md:text-6xl lg:text-7xl">
                {t("contact.hero.title")}{" "}
                <span className="text-yellow-400">Touch</span>
              </h1>
              <p className="mx-auto max-w-3xl text-xl text-gray-100 md:text-2xl">
                {t("contact.hero.subtitle")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-16 lg:grid-cols-2">
            {/* Contact Information Card */}
            <div className="space-y-8">
              <div className="rounded-2xl bg-white p-8 shadow-lg">
                <h2 className="mb-8 text-3xl font-bold text-green-800">
                  {t("contact.info.title")}
                </h2>

                {/* Contact Information */}
                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-center space-x-4">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg">
                      <FiPhone className="h-7 w-7" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {t("contact.info.phone.title")}
                      </h3>
                      <p className="text-gray-600">
                        {t("contact.info.phone.number")}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t("contact.info.phone.hours")}
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-center space-x-4">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
                      <FiMail className="h-7 w-7" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {t("contact.info.email.title")}
                      </h3>
                      <p className="text-gray-600">
                        {t("contact.info.email.address")}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t("contact.info.email.response")}
                      </p>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-center space-x-4">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
                      <FiMapPin className="h-7 w-7" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {t("contact.info.address.title")}
                      </h3>
                      <p className="text-gray-600">
                        {t("contact.info.address.location")}
                        <br />
                        {t("contact.info.address.state")}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t("contact.info.address.visit")}
                      </p>
                    </div>
                  </div>

                  {/* Office Hours */}
                  <div className="flex items-center space-x-4">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-orange-600 to-orange-700 text-white shadow-lg">
                      <FiClock className="h-7 w-7" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {t("contact.info.hours.title")}
                      </h3>
                      <p className="text-gray-600">
                        {t("contact.info.hours.weekdays")}
                        <br />
                        {t("contact.info.hours.saturday")}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t("contact.info.hours.sunday")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-2xl bg-white p-8 shadow-xl">
              <div className="mb-8">
                <h2 className="mb-2 text-3xl font-bold text-gray-800">
                  {t("contact.form.title")}
                </h2>
                <p className="text-gray-600">{t("contact.form.description")}</p>
              </div>

              {/* Status Messages */}
              {submitStatus === "success" && (
                <motion.div
                  className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4 text-green-700"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={transitions.fast}
                >
                  <div className="flex items-center space-x-2">
                    <FiCheckCircle className="h-5 w-5" />
                    <span className="font-medium">
                      {t("contact.form.success.title")}
                    </span>
                  </div>
                  <p className="mt-1 text-sm">
                    {t("contact.form.success.message")}
                  </p>
                </motion.div>
              )}

              {submitStatus === "error" && (
                <motion.div
                  className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={transitions.fast}
                >
                  <div className="flex items-center space-x-2">
                    <FiAlertCircle className="h-5 w-5" />
                    <span className="font-medium">
                      {t("contact.form.error.title")}
                    </span>
                  </div>
                  <p className="mt-1 text-sm">
                    {t("contact.form.error.message")}
                  </p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="name"
                      className="mb-2 block text-sm font-semibold text-gray-700"
                    >
                      {t("contact.form.fields.name")} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder={t("contact.form.fields.placeholders.name")}
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full rounded-xl border-2 px-4 py-3 text-gray-700 transition-all duration-200 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-100 ${
                        errors.name
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                      aria-describedby={errors.name ? "name-error" : undefined}
                    />
                    {errors.name && (
                      <motion.p
                        id="name-error"
                        className="mt-2 text-sm text-red-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={transitions.fast}
                      >
                        {errors.name}
                      </motion.p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-semibold text-gray-700"
                    >
                      {t("contact.form.fields.email")} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder={t("contact.form.fields.placeholders.email")}
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full rounded-xl border-2 px-4 py-3 text-gray-700 transition-all duration-200 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-100 ${
                        errors.email
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                      aria-describedby={
                        errors.email ? "email-error" : undefined
                      }
                    />
                    {errors.email && (
                      <motion.p
                        id="email-error"
                        className="mt-2 text-sm text-red-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={transitions.fast}
                      >
                        {errors.email}
                      </motion.p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="mb-2 block text-sm font-semibold text-gray-700"
                  >
                    {t("contact.form.fields.subject")} *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    placeholder={t("contact.form.fields.placeholders.subject")}
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full rounded-xl border-2 px-4 py-3 text-gray-700 transition-all duration-200 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-100 ${
                      errors.subject
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                    aria-describedby={
                      errors.subject ? "subject-error" : undefined
                    }
                  />
                  {errors.subject && (
                    <motion.p
                      id="subject-error"
                      className="mt-2 text-sm text-red-600"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={transitions.fast}
                    >
                      {errors.subject}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="mb-2 block text-sm font-semibold text-gray-700"
                  >
                    {t("contact.form.fields.message")} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder={t("contact.form.fields.placeholders.message")}
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full resize-none rounded-xl border-2 px-4 py-3 text-gray-700 transition-all duration-200 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-100 ${
                      errors.message
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                    aria-describedby={
                      errors.message ? "message-error" : undefined
                    }
                  ></textarea>
                  {errors.message && (
                    <motion.p
                      id="message-error"
                      className="mt-2 text-sm text-red-600"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={transitions.fast}
                    >
                      {errors.message}
                    </motion.p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={`group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-green-600 to-green-700 px-8 py-4 font-semibold text-white shadow-lg transition-all duration-300 ${
                    isSubmitting
                      ? "cursor-not-allowed opacity-50"
                      : "hover:from-green-700 hover:to-green-800 hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-green-200"
                  }`}
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                  transition={transitions.fast}
                >
                  <div className="flex items-center justify-center space-x-2">
                    {isSubmitting ? (
                      <>
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span>{t("contact.form.sending")}</span>
                      </>
                    ) : (
                      <>
                        <FiSend className="h-5 w-5" />
                        <span>{t("contact.form.submit")}</span>
                      </>
                    )}
                  </div>
                </motion.button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
